import { EmptyState } from '@/components/Molecules/EmptyState/EmptyState';
import type { IPaginationProps } from '@/components/Molecules/Pagination/Pagination';
import { Pagination } from '@/components/Molecules/Pagination/Pagination';
import { Stack } from '@/components/Molecules/Stack/Stack';
import { ContentItemCard } from '@/components/Organisms/ContentItemCard/ContentItemCard';
import useTranslation from '@/hooks/useTranslation';
import type { IContentItem, IPagination } from '@/interfaces';
import { calculateListStart } from '@/utils/siteUtils';
import type { ComponentProps, FC } from 'react';
import styles from './ResultList.module.css';

/**
 * A simple vertical list of ContentItem results
 */
export const ResultList: FC<IProps> = ({ items, pagination, urlTemplate, paginationOptions = {}, cardProps = {} }) => {
    const { t } = useTranslation('common');
    if (!items) {
        return <p>{t('Feedback.unexpected_error')}</p>;
    }

    if (items.length === 0) {
        return <EmptyState />;
    }

    const startIndex = calculateListStart(pagination);

    return (
        <Stack double>
            <ol className={styles.results} start={startIndex}>
                {items.map((item, index) => {
                    return (
                        <li key={item.url}>
                            <ContentItemCard
                                analyticsBaseId={`${item.type}-${item.id}`}
                                item={item}
                                key={item.url}
                                headingLevel="h3"
                                withSummary
                                position={startIndex + index}
                                {...cardProps}
                            />
                        </li>
                    );
                })}
            </ol>
            {pagination && urlTemplate && (
                <Pagination
                    total={pagination.total}
                    currentPage={pagination.current}
                    pageSize={pagination.pageSize}
                    urlTemplate={urlTemplate}
                    {...paginationOptions}
                />
            )}
        </Stack>
    );
};

interface IProps {
    /** An array of Content Items to become a list */
    items: IContentItem[];
    pagination?: IPagination;
    urlTemplate?: IPaginationProps['urlTemplate'];
    paginationOptions?: Partial<IPaginationProps>;
    cardProps?: Partial<ComponentProps<typeof ContentItemCard>>;
}
